import axios from 'axios';

const BASE_URL = process.env.REACT_APP_CLIENT_AGENT;

const agenticApi = {
  // 1. Dashboard
  getDashboard: async (limit = 10) => {
    const response = await axios.get(`${BASE_URL}/agentic-dashboard?limit=${limit}`);
    return response.data;
  },

  // 2. Supervisor Workspace - Pending Approvals
  getPendingApprovals: async (approverId, limit = 100) => {
    const response = await axios.get(`${BASE_URL}/supervisor-workspace/pending-approvals`, {
      params: { approver_id: approverId, limit }
    });
    return response.data;
  },

  // 2. PRD to Feature Requests
  prdFeatureRequests: async (payload) => {
    if (payload.document instanceof File) {
      const formData = new FormData();
      Object.keys(payload).forEach(key => {
        if (payload[key] !== undefined) formData.append(key, payload[key]);
      });
      const response = await axios.post(`${BASE_URL}/prd-feature-requests`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } else {
      const response = await axios.post(`${BASE_URL}/prd-feature-requests`, payload);
      return response.data;
    }
  },

  // 3. Feature Submission (All-in-one)
  submitFeature: async (payload) => {
    if (payload.document instanceof File) {
      const formData = new FormData();
      Object.keys(payload).forEach(key => {
        if (payload[key] !== undefined) formData.append(key, payload[key]);
      });
      const response = await axios.post(`${BASE_URL}/feature-submission`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } else {
      const response = await axios.post(`${BASE_URL}/feature-submission`, payload);
      return response.data;
    }
  },

  // 4. Get Generated Feature Requests (legacy — returns /feature-requests)
  getWorkflowFeatures: async (workflowId) => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows/${workflowId}/feature-requests`);
    return response.data;
  },

  // 4b. Get PRD Feature Requests with selection-status metadata (2026-06-03)
  // Returns selection_status_counts, selection_hint, and per-feature selection_status
  getWorkflowFeaturesV2: async (workflowId) => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows/${workflowId}/features`);
    return response.data;
  },

  // 5. Submit Selected Feature Requests (Creates child workflow)
  submitSelectedFeatures: async (workflowId, payload) => {
    const response = await axios.post(`${BASE_URL}/agentic-workflows/${workflowId}/submit-selected-features`, payload);
    return response.data;
  },

  // 6. Submit/Deploy on Same Workflow
  submitFeaturesSameWorkflow: async (workflowId, payload) => {
    const response = await axios.post(`${BASE_URL}/agentic-workflows/${workflowId}/submit-features`, payload);
    return response.data;
  },

  // 7. Deploy or Retry Deployment
  deployApp: async (workflowId, payload = {}) => {
    const response = await axios.post(`${BASE_URL}/agentic-workflows/${workflowId}/deploy-app`, payload);
    return response.data;
  },

  // 8. Workflow Overview (Recommended for detail screen)
  getWorkflowOverview: async (workflowId) => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows/${workflowId}/overview`);
    return response.data;
  },

  // 9. Full Workflow Detail
  getWorkflowDetail: async (workflowId) => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows/${workflowId}`);
    return response.data;
  },

  // 10. Stage Artifacts (Code/Docs)
  getStageArtifacts: async (workflowId, stageName) => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows/${workflowId}/stages/${stageName}/artifacts`);
    return response.data;
  },

  // 10. Feature Status
  getFeatureStatus: async (taskId) => {
    const response = await axios.get(`${BASE_URL}/feature-status/${taskId}`);
    return response.data;
  },

  // 11. Feature/Workflow Approval
  approveFeature: async (taskId, action, details = {}) => {
    const response = await axios.post(`${BASE_URL}/feature-approval/${taskId}`, {
      action,
      ...details
    });
    return response.data;
  },

  approveWorkflow: async (workflowId, action, details = {}) => {
    const response = await axios.post(`${BASE_URL}/agentic-workflows/${workflowId}/approval`, {
      action,
      ...details
    });
    return response.data;
  },

  // 12. Supporting APIs
  getWorkflows: async () => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows`);
    return response.data;
  },

  getStories: async (workflowId) => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows/${workflowId}/stories`);
    return response.data;
  },

  // Edit a PRD-analyzed feature after upload/analyze, before generation (2026-06-03)
  // Editable fields: title, description, acceptance_criteria, source_excerpt, status
  // Pass allow_edit_submitted: true (after confirmation) to edit a submitted feature
  editFeature: async (workflowId, storyIdOrKey, payload) => {
    const response = await axios.patch(
      `${BASE_URL}/agentic-workflows/${workflowId}/stories/${storyIdOrKey}`,
      payload
    );
    return response.data;
  },

  getTemplates: async () => {
    const response = await axios.get(`${BASE_URL}/workflow-templates`);
    return response.data;
  },

  previewTemplate: async (payload) => {
    const response = await axios.post(`${BASE_URL}/workflow-templates/preview`, payload);
    return response.data;
  },

  // 13. Spec Kit Workspaces
  getSpecKitFeatures: async () => {
    const response = await axios.get(`${BASE_URL}/speckit/features`);
    return response.data;
  },

  getSpecKitStatus: async (featureId) => {
    const url = featureId ? `${BASE_URL}/speckit/status?feature_id=${featureId}` : `${BASE_URL}/speckit/status`;
    const response = await axios.get(url);
    return response.data;
  },

  getSpecKitStatusByWorkflow: async (workflowId) => {
    const response = await axios.get(`${BASE_URL}/speckit/status`, { params: { workflow_id: workflowId } });
    return response.data;
  },

  getSpecKitFeature: async (featureId) => {
    const response = await axios.get(`${BASE_URL}/speckit/features/${featureId}`);
    return response.data;
  },

  getSpecKitValidation: async (featureId) => {
    const url = featureId ? `${BASE_URL}/speckit/validation?feature_id=${featureId}` : `${BASE_URL}/speckit/validation`;
    const response = await axios.get(url);
    return response.data;
  },

  // 13.5 New Workflow-Driven Spec Kit APIs
  getSpecKitForWorkflow: async (workflowId, fullUrl = null) => {
    const url = fullUrl || `${BASE_URL}/agentic-workflows/${workflowId}/speckit`;
    const response = await axios.get(url);
    return response.data;
  },

  getSpecKitValidationForWorkflow: async (workflowId, fullUrl = null) => {
    const url = fullUrl || `${BASE_URL}/agentic-workflows/${workflowId}/speckit/validation`;
    const response = await axios.get(url);
    return response.data;
  },

  // 14. Observability & Health
  getWorkflowHealth: async (workflowId) => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows/${workflowId}/health`);
    return response.data;
  },

  getWorkflowReleases: async (workflowId) => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows/${workflowId}/releases`);
    return response.data;
  },

  downloadSpecUrl: (workflowId, format = 'md') => {
    return `${BASE_URL}/agentic-workflows/${workflowId}/spec/download?format=${format}`;
  },

  // 15. Delivery Queue & Delivery Details
  getDeliveryQueue: async (params = {}) => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows/delivery-queue`, { params });
    return response.data;
  },

  getDeliveryDetail: async (workflowId) => {
    const response = await axios.get(`${BASE_URL}/agentic-workflows/${workflowId}/delivery`);
    return response.data;
  },

  refreshSqliteQa: async (workflowId) => {
    const response = await axios.post(`${BASE_URL}/agentic-workflows/${workflowId}/qa/sqlite/refresh`, {});
    return response.data;
  },

  createSupervisorHandoff: async (workflowId) => {
    const response = await axios.post(`${BASE_URL}/agentic-workflows/${workflowId}/supervisor-handoff`, {});
    return response.data;
  },

  regenerateWorkflow: async (workflowId, payload) => {
    const response = await axios.post(`${BASE_URL}/agentic-workflows/${workflowId}/supervisor-feedback/regenerate`, payload);
    return response.data;
  },

  downloadCodeUrl: (workflowId, redirect = false) => {
    return `${BASE_URL}/agentic-workflows/${workflowId}/code/download${redirect ? '?redirect=false' : ''}`;
  }
};

export default agenticApi;
